import React from 'react';
import { Link } from 'react-router-dom';

// A deliberately small Markdown subset for news post bodies, rendered straight
// to React elements. No HTML string is ever produced or injected, so a post body
// can't smuggle in markup or script — the worst a bad body can do is render
// literal text. It also renders identically under renderToString, so prerendered
// articles hydrate cleanly.
//
// Supported (the editor's formatting help in src/pages/app/PostEditor.js lists
// the same set — keep them in sync):
//   # Heading / ## Subheading / ### Small heading   (rendered h2 / h3 / h4)
//   **bold**, *italic* or _italic_, [link text](https://…)
//   - bullet / * bullet, 1. numbered
//   > quote
//   ![alt text](https://…image)  on its own line
//   ---  (divider)
// A single line break inside a paragraph is kept as a line break, which is
// what non-technical writers expect from a plain textarea.

const SAFE_LINK = /^(https?:\/\/|mailto:|tel:|\/(?!\/)|#)/i;
const SAFE_IMAGE = /^(https?:\/\/|\/(?!\/))/i;

// `_italic_` only at word edges, so snake_case words and URLs with underscores
// typed as plain text keep their underscores.
const INLINE = /\*\*(.+?)\*\*|\*(?!\s)(.+?)\*|(?<![\w])_(?!\s)(.+?)_(?![\w])|\[([^\]]+)\]\(([^)\s]+)\)/g;

function renderInline(text, keyPrefix) {
  const out = [];
  let last = 0;
  let i = 0;
  text.replace(INLINE, (match, bold, italic, italicUnderscore, linkText, href, offset) => {
    if (offset > last) out.push(text.slice(last, offset));
    const key = `${keyPrefix}-${i++}`;
    if (bold !== undefined) {
      out.push(<strong key={key}>{renderInline(bold, key)}</strong>);
    } else if (italic !== undefined || italicUnderscore !== undefined) {
      out.push(<em key={key}>{renderInline(italic ?? italicUnderscore, key)}</em>);
    } else if (!SAFE_LINK.test(href)) {
      // Unknown scheme (javascript:, data:, …): keep the words, drop the link.
      out.push(...[].concat(renderInline(linkText, key)));
    } else if (href.startsWith('/') || href.startsWith('#')) {
      out.push(
        <Link key={key} to={href}>
          {renderInline(linkText, key)}
        </Link>
      );
    } else {
      out.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {renderInline(linkText, key)}
        </a>
      );
    }
    last = offset + match.length;
    return match;
  });
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Keeps single newlines inside a paragraph as <br>.
function renderLines(lines, keyPrefix) {
  return lines.flatMap((line, i) => {
    const parts = renderInline(line, `${keyPrefix}-${i}`);
    return i === 0 ? parts : [<br key={`${keyPrefix}-br-${i}`} />, ...parts];
  });
}

const HEADING = /^(#{1,3})\s+(.*)$/;
const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const IMAGE = /^!\[([^\]]*)\]\(([^)\s]+)\)$/;
const RULE = /^(-{3,}|\*{3,})$/;

function parseBlocks(source) {
  const lines = String(source || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i += 1;
      continue;
    }
    let m;
    if ((m = line.match(HEADING))) {
      blocks.push({ type: 'heading', level: m[1].length, text: m[2] });
      i += 1;
    } else if (RULE.test(line)) {
      blocks.push({ type: 'rule' });
      i += 1;
    } else if ((m = line.match(IMAGE))) {
      blocks.push({ type: 'image', alt: m[1], src: m[2] });
      i += 1;
    } else if (QUOTE.test(line)) {
      const quoted = [];
      while (i < lines.length && QUOTE.test(lines[i].trim())) {
        quoted.push(lines[i].trim().match(QUOTE)[1]);
        i += 1;
      }
      blocks.push({ type: 'quote', lines: quoted });
    } else if (BULLET.test(line) || NUMBERED.test(line)) {
      const ordered = NUMBERED.test(line);
      const pattern = ordered ? NUMBERED : BULLET;
      const items = [];
      while (i < lines.length && pattern.test(lines[i])) {
        items.push(lines[i].match(pattern)[1]);
        i += 1;
      }
      blocks.push({ type: 'list', ordered, items });
    } else {
      const para = [];
      while (i < lines.length) {
        const next = lines[i].trim();
        if (
          !next ||
          HEADING.test(next) ||
          RULE.test(next) ||
          IMAGE.test(next) ||
          QUOTE.test(next) ||
          BULLET.test(lines[i]) ||
          NUMBERED.test(lines[i])
        ) {
          break;
        }
        para.push(next);
        i += 1;
      }
      blocks.push({ type: 'paragraph', lines: para });
    }
  }
  return blocks;
}

/** Renders a Markdown-subset string to an array of React elements. */
export function renderMarkdown(source) {
  return parseBlocks(source).map((block, i) => {
    const key = `b${i}`;
    switch (block.type) {
      case 'heading': {
        const Tag = `h${block.level + 1}`;
        return <Tag key={key}>{renderInline(block.text, key)}</Tag>;
      }
      case 'rule':
        return <hr key={key} />;
      case 'image':
        if (!SAFE_IMAGE.test(block.src)) return null;
        return (
          <figure key={key}>
            <img src={block.src} alt={block.alt} loading="lazy" decoding="async" />
            {block.alt && <figcaption>{block.alt}</figcaption>}
          </figure>
        );
      case 'quote':
        return <blockquote key={key}>{renderLines(block.lines, key)}</blockquote>;
      case 'list': {
        const Tag = block.ordered ? 'ol' : 'ul';
        return (
          <Tag key={key}>
            {block.items.map((item, j) => (
              <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
            ))}
          </Tag>
        );
      }
      default:
        return <p key={key}>{renderLines(block.lines, key)}</p>;
    }
  });
}

/** Strips the Markdown syntax, leaving readable plain text (excerpts, word counts). */
export function markdownToPlainText(source) {
  return String(source || '')
    .replace(/^!\[[^\]]*\]\([^)]*\)$/gm, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,3}\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/^\s*([-*]|\d+[.)])\s+/gm, '')
    .replace(/^(-{3,}|\*{3,})$/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(?!\s)(.+?)\*/g, '$1')
    .replace(/(?<![\w])_(?!\s)(.+?)_(?![\w])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}
